/**
 * TravelersPreferencesContext
 * 
 * Oda bazlı cookie yönetimini merkezileştiren React Context
 * SIFIRDAN YENİDEN YAZILDI - Geriye dönük uyumluluk YOK
 */

'use client'

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { addDays } from 'date-fns'
import { 
    getTravelersPreferences as getCookiePreferences, 
    setTravelersPreferences as setCookiePreferences,
} from '@/lib/cookies'
import { toLocalDateString } from '@/lib/date-utils'
import type { TravelersPreferences, Room } from '@/types/travelers'
import { DEFAULT_DEPARTURE_DAYS_AHEAD, DEFAULT_INITIAL_DEPARTURE_DAYS_AHEAD, DEFAULT_PAX_COUNT } from '@/constants/pricing'

interface TravelersPreferencesContextType {
    preferences: TravelersPreferences | null
    updatePreferences: (updates: Partial<TravelersPreferences>) => void
    addRoom: () => void
    removeRoom: (index: number) => void
    updateRoom: (index: number, room: Room) => void
    setDepartureDate: (date: Date | null) => void
    setProductType: (type: 'tour' | 'ship') => void
    clearPreferences: () => void
}

const TravelersPreferencesContext = createContext<TravelersPreferencesContextType | undefined>(undefined)

export function TravelersPreferencesProvider({ children }: { children: React.ReactNode }) {
    const addRoomLockRef = useRef<boolean>(false)
    const isUpdatingCookieRef = useRef<boolean>(false) // Cookie güncellemesi sırasında event'i ignore et

    // İlk ziyarette (çerez yokken) fiyatların doğru görünmesi için 2 hafta sonrası tarih
    const getDefaultDepartureDate = useCallback((): string => {
        return toLocalDateString(addDays(new Date(), DEFAULT_INITIAL_DEPARTURE_DAYS_AHEAD))
    }, [])

    // Varsayılan preferences objesi: çerez yokken 2 hafta sonrası tarih + 2 yetişkin (fiyat gösterimi için)
    const getDefaultPreferences = useCallback((): TravelersPreferences => {
        return {
            product_type: 'tour',
            departure_date: getDefaultDepartureDate(),
            rooms: [
                {
                    adults: DEFAULT_PAX_COUNT,
                    children: 0,
                    childAges: [],
                },
            ],
        }
    }, [getDefaultDepartureDate])

    // SSR-safe: SSR'da null, client'ta useEffect ile yükle (hydration hatasını önler)
    const [preferences, setPreferences] = useState<TravelersPreferences | null>(null)
    const isInitializedRef = useRef<boolean>(false)

    // Client'ta cookie'den oku veya default değerleri set et (sadece bir kez)
    useEffect(() => {
        // Strict Mode'da iki kez çalışmasını önle
        if (isInitializedRef.current || typeof window === 'undefined') return
        isInitializedRef.current = true
        
        const cookiePrefs = getCookiePreferences()
        if (cookiePrefs) {
            // Cookie'deki oda sayısı korunur (2+ oda da kalır, sayfa yenilense bile)
            setPreferences(cookiePrefs)
        } else {
            // Çerez yokken: 2 hafta sonrası tarih + 2 yetişkin (fiyatların doğru görünmesi için)
            const defaultPrefs = getDefaultPreferences()
            setCookiePreferences(defaultPrefs)
            setPreferences(defaultPrefs)
        }
    }, [])

    // Cookie'yi güncelle ve state'i senkronize et
    const updatePreferences = useCallback((updates: Partial<TravelersPreferences>) => {
        setPreferences((prev) => {
            if (!prev) {
                const defaultPrefs = getDefaultPreferences()
                const newPrefs = { ...defaultPrefs, ...updates }
                // Cookie güncellemesi sırasında event listener'ın tekrar setPreferences çağırmasını önle
                isUpdatingCookieRef.current = true
                setCookiePreferences(newPrefs)
                setTimeout(() => {
                    isUpdatingCookieRef.current = false
                }, 100)
                return newPrefs
            }

            const newPrefs: TravelersPreferences = {
                ...prev,
                ...updates,
            }
            
            // Cookie güncellemesi sırasında event listener'ın tekrar setPreferences çağırmasını önle
            isUpdatingCookieRef.current = true
            setCookiePreferences(newPrefs)
            setTimeout(() => {
                isUpdatingCookieRef.current = false
            }, 100)
            
            return newPrefs
        })
    }, [getDefaultPreferences])

    // Oda ekle (maksimum 5) - çift tıklama koruması ile
    const addRoom = useCallback(() => {
        // Çift tıklama koruması - lock aktifse direkt çık
        if (addRoomLockRef.current) {
            return
        }
        
        // Lock'u hemen aktif et
        addRoomLockRef.current = true
        
        setPreferences((prev) => {
            if (!prev) {
                // State yoksa lock'u kaldır ve çık
                setTimeout(() => {
                    addRoomLockRef.current = false
                }, 100)
                return prev
            }
            
            if (prev.rooms.length >= 5) {
                // Maksimum 5 oda kontrolü - lock'u kaldır
                setTimeout(() => {
                    addRoomLockRef.current = false
                }, 100)
                return prev
            }

            const newRooms = [
                ...prev.rooms,
                {
                    adults: 2,
                    children: 0,
                    childAges: [],
                },
            ]

            const newPrefs: TravelersPreferences = {
                ...prev,
                rooms: newRooms,
            }

            // Cookie güncellemesi sırasında event listener'ın tekrar setPreferences çağırmasını önle
            isUpdatingCookieRef.current = true
            setCookiePreferences(newPrefs)
            
            // Cookie güncellemesi tamamlandıktan sonra flag'i kaldır
            // setTimeout ile biraz gecikme ver ki event listener çalışmadan önce flag set olsun
            setTimeout(() => {
                isUpdatingCookieRef.current = false
                addRoomLockRef.current = false
            }, 100) // Event listener'ın çalışması için yeterli süre
            
            return newPrefs
        })
    }, [])

    // Oda sil (minimum 1)
    const removeRoom = useCallback((index: number) => {
        setPreferences((prev) => {
            if (!prev) return prev
            
            if (prev.rooms.length <= 1) {
                // Minimum 1 oda kontrolü
                return prev
            }

            const newRooms = prev.rooms.filter((_, i) => i !== index)
            const newPrefs: TravelersPreferences = {
                ...prev,
                rooms: newRooms,
            }

            // Cookie güncellemesi sırasında event listener'ın tekrar setPreferences çağırmasını önle
            isUpdatingCookieRef.current = true
            setCookiePreferences(newPrefs)
            setTimeout(() => {
                isUpdatingCookieRef.current = false
            }, 100)
            
            return newPrefs
        })
    }, [])

    // Oda güncelle
    const updateRoom = useCallback((index: number, room: Room) => {
        setPreferences((prev) => {
            if (!prev) return prev

            const newRooms = [...prev.rooms]
            newRooms[index] = room

            const newPrefs: TravelersPreferences = {
                ...prev,
                rooms: newRooms,
            }

            // Cookie güncellemesi sırasında event listener'ın tekrar setPreferences çağırmasını önle
            isUpdatingCookieRef.current = true
            setCookiePreferences(newPrefs)
            setTimeout(() => {
                isUpdatingCookieRef.current = false
            }, 100)
            
            return newPrefs
        })
    }, [])

    // Çıkış tarihi set et
    const setDepartureDate = useCallback((date: Date | null) => {
        if (date) {
            const dateString = date.toISOString().split('T')[0]
            updatePreferences({ departure_date: dateString })
        } else {
            updatePreferences({ departure_date: undefined })
        }
    }, [updatePreferences])

    // Product type set et
    const setProductType = useCallback((type: 'tour' | 'ship') => {
        updatePreferences({ product_type: type })
    }, [updatePreferences])

    // Preferences'ı temizle (varsayılan değerlere dön)
    const clearPreferences = useCallback(() => {
        const defaultPrefs = getDefaultPreferences()
        setPreferences(defaultPrefs)
        setCookiePreferences(defaultPrefs)
    }, [getDefaultPreferences])

    // Cookie değişikliklerini dinle (diğer tab'lardan gelen değişiklikler için)
    useEffect(() => {
        // Sadece client'ta ve initialization tamamlandıktan sonra dinle
        if (typeof window === 'undefined' || !isInitializedRef.current) return
        
        const handleCookieChange = () => {
            // Eğer biz cookie'yi güncelliyorsak, event'i ignore et (sonsuz döngüyü önle)
            if (isUpdatingCookieRef.current) return
            
            // Cookie change listener'da 2+ oda kontrolü YAPMA - kullanıcının eklediği odaları koru
            // Bu listener sadece diğer tab'lardan gelen değişiklikleri senkronize etmek için
            const cookiePrefs = getCookiePreferences()
            if (cookiePrefs) {
                setPreferences(cookiePrefs)
            }
        }

        window.addEventListener('travelersPreferencesChanged', handleCookieChange)
        return () => {
            window.removeEventListener('travelersPreferencesChanged', handleCookieChange)
        }
    }, [])

    return (
        <TravelersPreferencesContext.Provider
            value={{
                preferences,
                updatePreferences,
                addRoom,
                removeRoom,
                updateRoom,
                setDepartureDate,
                setProductType,
                clearPreferences,
            }}
        >
            {children}
        </TravelersPreferencesContext.Provider>
    )
}

/**
 * TravelersPreferencesContext'i kullanmak için custom hook
 */
export function useTravelersPreferences() {
    const context = useContext(TravelersPreferencesContext)
    
    // GrapesJS / render-component gibi Next uygulaması dışındaki
    // server-side render ortamlarda provider sarmalanmıyor.
    // Bu durumlarda güvenli bir fallback ile varsayılan değerleri kullanıyoruz.
    if (context === undefined) {
        const isServerSide = typeof window === "undefined"
        const isGrapesPreview =
            typeof globalThis !== "undefined" &&
            (globalThis as typeof globalThis & { __GRAPES_PREVIEW__?: boolean })
                .__GRAPES_PREVIEW__ === true

        if (isServerSide || isGrapesPreview) {
            // Varsayılan preferences objesi
            const defaultDepartureDate = toLocalDateString(addDays(new Date(), DEFAULT_DEPARTURE_DAYS_AHEAD))
            const defaultPreferences: TravelersPreferences = {
                product_type: 'tour',
                departure_date: defaultDepartureDate,
                rooms: [
                    {
                        adults: 2,
                        children: 0,
                        childAges: [],
                    },
                ],
            }

            const fallback: TravelersPreferencesContextType = {
                preferences: defaultPreferences,
                updatePreferences: () => {
                    // no-op – GrapesJS önizlemesinde cookie güncellemesi yapmıyoruz
                },
                addRoom: () => {
                    // no-op
                },
                removeRoom: () => {
                    // no-op
                },
                updateRoom: () => {
                    // no-op
                },
                setDepartureDate: () => {
                    // no-op
                },
                setProductType: () => {
                    // no-op
                },
                clearPreferences: () => {
                    // no-op
                },
            }

            return fallback
        }

        // Gerçek frontend tarafında provider olmadan kullanılırsa
        // hâlâ net bir hata verelim.
        throw new Error('useTravelersPreferences must be used within a TravelersPreferencesProvider')
    }

    return context
}
